import { useMemo } from 'react';
import {
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import styles from './OutgoingShipmentOverviewTabView.module.scss';

// FieldSection renders an element `value` as-is, so the weight has to be set here.
const quantityValue = (amount) => (
  <Typography
    variant={TypographyVariants.bodySm}
    weight={TypographyWeights.semibold}
    color={TypographyColors.primary}
  >
    {amount}
  </Typography>
);

/**
 * Props:
 *   shipment  the outgoing-shipment detail view model
 */
export const OutgoingShipmentOverviewTabView = ({ shipment }) => {
  const t = useTranslations();

  const working = shipment.byUnit?.[UNIT_TYPES.working];
  const declaration = shipment.byUnit?.[UNIT_TYPES.declaration];

  const workingSectionFields = useMemo(
    () => [
      { label: t('FIELD_SHIPMENT_QUANTITY'), value: quantityValue(working?.shipmentQuantity) },
      { label: t('FIELD_ALLOCATED_QUANTITY'), value: quantityValue(working?.allocatedQuantity) },
      {
        label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
        value: quantityValue(working?.remainingToAllocateQuantity),
      },
    ],
    [t, working],
  );

  const declarationSectionFields = useMemo(
    () => [
      { label: t('FIELD_SHIPMENT_QUANTITY'), value: quantityValue(declaration?.shipmentQuantity) },
      {
        label: t('FIELD_ALLOCATED_QUANTITY'),
        value: quantityValue(declaration?.allocatedQuantity),
      },
      {
        label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
        value: quantityValue(declaration?.remainingToAllocateQuantity),
      },
    ],
    [t, declaration],
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.primary}
        >
          {t('OUTGOING_SHIPMENTS_DETAIL_TAB_OVERVIEW')}
        </Typography>
      </div>
      <div className={styles.cardBody}>
        {/* Absent unless the material carries a working-unit correlation — byUnit omits the unit entirely. */}
        {working && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_WORKING_UNIT')}
            fields={workingSectionFields}
          />
        )}
        {declaration && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_DECLARATION_UNIT')}
            fields={declarationSectionFields}
          />
        )}
      </div>
    </div>
  );
};
